import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getClientiRete, isAccessoSospeso } from "@/api/rete";
import type { ClienteRete } from "@/types/rete";
import { AccessoSospeso } from "./AccessoSospeso";
import { CHIAVE_AGENZIA, FiltroAgenzia } from "./FiltroAgenzia";
import { PAGE_SIZE, Paginazione, paginaDaUrl } from "./Paginazione";

const CAMPO = "min-h-[44px] px-3 py-2 border border-border rounded-lg text-sm bg-card";

function nomeCliente(c: ClienteRete): string {
  return c.nome_visualizzato || [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
}

export function ClientiRete() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const agenzia = sp.get(CHIAVE_AGENZIA) ?? "";
  const q = sp.get("q") ?? "";
  const pagina = paginaDaUrl(sp);
  const [pendingSearch, setPendingSearch] = useState(q);
  const [items, setItems] = useState<ClienteRete[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getClientiRete({
      limit: PAGE_SIZE,
      offset: pagina * PAGE_SIZE,
      q: q || undefined,
      agenzia_id: agenzia || null,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else setError("Impossibile caricare i clienti.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agenzia, q, pagina]);

  function cerca(e: React.FormEvent) {
    e.preventDefault();
    const valore = pendingSearch.trim();
    setSp((prev) => {
      if (valore) prev.set("q", valore);
      else prev.delete("q");
      prev.delete("pagina");
      return prev;
    });
  }

  function apri(id: string) {
    navigate(`/rete/clienti/${encodeURIComponent(id)}`);
  }

  if (sospeso) return <AccessoSospeso />;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-text">Clienti della rete</h2>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <FiltroAgenzia />
        <form onSubmit={cerca} className="flex gap-2 flex-1">
          <input
            type="search"
            aria-label="Cerca"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            placeholder="Nome o email"
            className={`${CAMPO} flex-1 min-w-0`}
          />
          <button type="submit" className="min-h-[44px] px-4 bg-brand text-white rounded-lg text-sm flex-shrink-0">
            Cerca
          </button>
        </form>
      </div>

      {error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : items.length === 0 ? (
        <EmptyState icon={<Users size={26} className="text-brand" />} title="Nessun cliente" />
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Agenzia</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead className="hidden md:table-cell">Telefono</TableHead>
                  <TableHead className="text-right">Pratiche</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => apri(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        apri(c.id);
                      }
                    }}
                    className="cursor-pointer [&_td]:py-3"
                  >
                    <TableCell className="font-medium text-text">{nomeCliente(c)}</TableCell>
                    <TableCell>{c.agenzia_nome ?? "—"}</TableCell>
                    <TableCell className="hidden md:table-cell">{c.email ?? "—"}</TableCell>
                    <TableCell className="hidden md:table-cell tabular-nums">{c.phone ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.opportunities_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Paginazione total={total} loading={loading} />
    </div>
  );
}
